import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSEO } from '@/hooks/useSEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertCircle, CheckCircle, TrendingUp, DollarSign, Clock, Users, Bot, FileText, Download, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import jsPDF from 'jspdf';

const RPAAssessment = () => {
  const { language, t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any>(null);

  // SEO Optimization
  useSEO({
    title: language === 'en' 
      ? "🆓 FREE RPA Assessment | Save 60% on Automation Costs | QuantumBox AI"
      : "🆓 Évaluation RPA GRATUITE | Économisez 60% sur l'Automatisation | QuantumBox AI",
    description: language === 'en'
      ? "Get your FREE RPA automation assessment! Discover how much you can save vs UiPath, Automation Anywhere & Blue Prism. Instant ROI analysis, cost comparison & implementation roadmap. 40-60% cost savings guaranteed with our Agentic AI approach."
      : "Obtenez votre évaluation d'automatisation RPA GRATUITE! Découvrez combien vous pouvez économiser vs UiPath, Automation Anywhere et Blue Prism. Analyse ROI instantanée, comparaison des coûts et feuille de route. 40-60% d'économies garanties.",
    keywords: language === 'en'
      ? "free RPA assessment, RPA cost comparison, UiPath alternative, Automation Anywhere alternative, Blue Prism alternative, RPA ROI calculator, agentic AI automation, business process automation, RPA savings calculator, automation feasibility assessment"
      : "évaluation RPA gratuite, comparaison coûts RPA, alternative UiPath, alternative Automation Anywhere, calculateur ROI RPA, automatisation IA agentique, évaluation faisabilité automatisation",
    ogTitle: language === 'en'
      ? "🆓 FREE RPA Assessment - Save 60% vs UiPath & Competitors"
      : "🆓 Évaluation RPA GRATUITE - Économisez 60% vs UiPath",
    ogDescription: language === 'en'
      ? "Compare RPA costs: QuantumBox AI vs UiPath, Automation Anywhere & Blue Prism. Get instant ROI analysis and save 40-60% on automation costs with our free assessment."
      : "Comparez les coûts RPA: QuantumBox AI vs UiPath, Automation Anywhere. Obtenez une analyse ROI instantanée et économisez 40-60% avec notre évaluation gratuite.",
    canonical: `${window.location.origin}/rpa-assessment`
  });

  // Add structured data for SEO
  React.useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": language === 'en' ? "RPA Assessment Tool" : "Outil d'Évaluation RPA",
      "description": language === 'en' 
        ? "Free RPA automation assessment tool to compare costs and ROI vs UiPath, Automation Anywhere, and Blue Prism"
        : "Outil gratuit d'évaluation d'automatisation RPA pour comparer les coûts et ROI",
      "url": `${window.location.origin}/rpa-assessment`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": language === 'en' ? "Free RPA Assessment" : "Évaluation RPA Gratuite"
      },
      "provider": {
        "@type": "Organization",
        "name": "QuantumBox AI",
        "url": window.location.origin
      },
      "featureList": [
        "Cost Comparison Analysis",
        "ROI Calculator", 
        "Implementation Roadmap",
        "Automation Feasibility Assessment",
        "Savings Analysis vs Competitors"
      ]
    };

    // Add specific RPA meta tags
    const rpaMeta = document.createElement('meta');
    rpaMeta.name = 'robots';
    rpaMeta.content = 'index, follow, max-snippet:-1, max-image-preview:large';
    document.head.appendChild(rpaMeta);

    const rpaSchema = document.createElement('script');
    rpaSchema.type = 'application/ld+json';
    rpaSchema.text = JSON.stringify(structuredData);
    document.head.appendChild(rpaSchema);

    return () => {
      if (document.head.contains(rpaMeta)) document.head.removeChild(rpaMeta);
      if (document.head.contains(rpaSchema)) document.head.removeChild(rpaSchema);
    };
  }, [language]);

  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    industry: '',
    
    // Company Profile
    company_size: '',
    annual_revenue: '',
    current_employees: '',
    it_budget: '',
    automation_budget: '',
    
    // Current Processes
    manual_processes: [] as string[],
    process_volume: '',
    process_frequency: '',
    current_tools: [] as string[],
    pain_points: '',
    
    // Complexity Assessment
    data_sources: [] as string[],
    integration_complexity: '',
    exception_handling: '',
    compliance_requirements: [] as string[],
    decision_complexity: '',
    
    // Technology Infrastructure
    current_platforms: [] as string[],
    cloud_readiness: '',
    security_requirements: '',
    it_support: '',
    change_management: '',
    
    // Business Goals
    primary_objectives: [] as string[],
    success_metrics: [] as string[],
    timeline: '',
    roi_expectations: '',
    stakeholder_buy_in: ''
  });

  const totalSteps = 6;

  const translations = {
    en: {
      title: 'RPA Automation Assessment',
      subtitle: 'Discover your automation potential and ROI with our comprehensive assessment',
      step: 'Step',
      of: 'of',
      next: 'Next',
      previous: 'Previous',
      submit: 'Get Results',
      submitting: 'Analyzing...',
      
      // Steps
      'step1.title': 'Personal Information',
      'step1.subtitle': 'Tell us about yourself and your organization',
      'step2.title': 'Company Profile',
      'step2.subtitle': 'Help us understand your business scale and budget',
      'step3.title': 'Current Processes',
      'step3.subtitle': 'Identify your manual processes and pain points',
      'step4.title': 'Complexity Assessment',
      'step4.subtitle': 'Evaluate the technical complexity of your processes',
      'step5.title': 'Technology Infrastructure',
      'step5.subtitle': 'Assess your current IT environment and capabilities',
      'step6.title': 'Business Goals',
      'step6.subtitle': 'Define your automation objectives and success criteria',
      
      // Results
      'results.title': 'Your RPA Assessment Results',
      'results.subtitle': 'Based on your inputs, here\'s your automation potential',
      'results.feasibility': 'Automation Feasibility',
      'results.ease': 'Implementation Ease',
      'results.roi': '3-Year ROI',
      'results.investment': 'Estimated Investment',
      'results.savings': 'Annual Cost Savings',
      'results.payback': 'Payback Period',
      'results.cost_comparison': 'Cost Comparison: QuantumBox vs Competitors',
      'results.quantum_advantage': 'QuantumBox Advantage',
      'results.download': 'Download Full Report',
      
      // Cost Comparison
      'comparison.title': 'Why Choose QuantumBox Over Traditional RPA?',
      'comparison.subtitle': 'See how our Agentic AI approach saves you money',
      'comparison.quantum': 'QuantumBox AI',
      'comparison.uipath': 'UiPath',
      'comparison.automation_anywhere': 'Automation Anywhere',
      'comparison.blue_prism': 'Blue Prism',
      'comparison.implementation': 'Implementation Cost',
      'comparison.licensing': 'Annual Licensing',
      'comparison.maintenance': 'Maintenance & Support',
      'comparison.training': 'Training Costs',
      'comparison.total_3yr': 'Total 3-Year Cost',
      'comparison.savings': 'vs QuantumBox Savings',
      
      // Form fields
      'form.name': 'Full Name',
      'form.email': 'Email Address',
      'form.phone': 'Phone Number',
      'form.company': 'Company Name',
      'form.role': 'Your Role',
      'form.industry': 'Industry',
      'form.company_size': 'Company Size',
      'form.annual_revenue': 'Annual Revenue',
      'form.current_employees': 'Number of Employees',
      'form.it_budget': 'Annual IT Budget',
      'form.automation_budget': 'Automation Budget',
      'form.manual_processes': 'Current Manual Processes',
      'form.process_volume': 'Process Volume',
      'form.process_frequency': 'Process Frequency',
      'form.current_tools': 'Current Tools',
      'form.pain_points': 'Main Pain Points',
      'form.data_sources': 'Data Sources',
      'form.integration_complexity': 'Integration Complexity',
      'form.exception_handling': 'Exception Handling',
      'form.compliance_requirements': 'Compliance Requirements',
      'form.decision_complexity': 'Decision Complexity',
      'form.current_platforms': 'Current Platforms',
      'form.cloud_readiness': 'Cloud Readiness',
      'form.security_requirements': 'Security Requirements',
      'form.it_support': 'IT Support Level',
      'form.change_management': 'Change Management',
      'form.primary_objectives': 'Primary Objectives',
      'form.success_metrics': 'Success Metrics',
      'form.timeline': 'Implementation Timeline',
      'form.roi_expectations': 'ROI Expectations',
      'form.stakeholder_buy_in': 'Stakeholder Buy-in'
    },
    fr: {
      title: 'Évaluation d\'Automatisation RPA',
      subtitle: 'Découvrez votre potentiel d\'automatisation et ROI avec notre évaluation complète',
      step: 'Étape',
      of: 'de',
      next: 'Suivant',
      previous: 'Précédent',
      submit: 'Obtenir les Résultats',
      submitting: 'Analyse...',
      
      // Steps
      'step1.title': 'Informations Personnelles',
      'step1.subtitle': 'Parlez-nous de vous et de votre organisation',
      'step2.title': 'Profil d\'Entreprise',
      'step2.subtitle': 'Aidez-nous à comprendre l\'échelle et le budget de votre entreprise',
      'step3.title': 'Processus Actuels',
      'step3.subtitle': 'Identifiez vos processus manuels et points de douleur',
      'step4.title': 'Évaluation de Complexité',
      'step4.subtitle': 'Évaluez la complexité technique de vos processus',
      'step5.title': 'Infrastructure Technologique',
      'step5.subtitle': 'Évaluez votre environnement IT actuel et capacités',
      'step6.title': 'Objectifs d\'Entreprise',
      'step6.subtitle': 'Définissez vos objectifs d\'automatisation et critères de succès',
      
      // Results
      'results.title': 'Vos Résultats d\'Évaluation RPA',
      'results.subtitle': 'Basé sur vos entrées, voici votre potentiel d\'automatisation',
      'results.feasibility': 'Faisabilité d\'Automatisation',
      'results.ease': 'Facilité d\'Implémentation',
      'results.roi': 'ROI sur 3 Ans',
      'results.investment': 'Investissement Estimé',
      'results.savings': 'Économies Annuelles',
      'results.payback': 'Période de Récupération',
      'results.cost_comparison': 'Comparaison de Coûts: QuantumBox vs Concurrents',
      'results.quantum_advantage': 'Avantage QuantumBox',
      'results.download': 'Télécharger le Rapport Complet',
      
      // Cost Comparison
      'comparison.title': 'Pourquoi Choisir QuantumBox Plutôt que RPA Traditionnel?',
      'comparison.subtitle': 'Voyez comment notre approche IA Agentique vous fait économiser',
      'comparison.quantum': 'QuantumBox AI',
      'comparison.uipath': 'UiPath',
      'comparison.automation_anywhere': 'Automation Anywhere',
      'comparison.blue_prism': 'Blue Prism',
      'comparison.implementation': 'Coût d\'Implémentation',
      'comparison.licensing': 'Licence Annuelle',
      'comparison.maintenance': 'Maintenance & Support',
      'comparison.training': 'Coûts de Formation',
      'comparison.total_3yr': 'Coût Total sur 3 Ans',
      'comparison.savings': 'vs Économies QuantumBox'
    },
    es: {
      title: 'Evaluación de Automatización RPA',
      subtitle: 'Descubre tu potencial de automatización y ROI con nuestra evaluación integral',
      step: 'Paso',
      of: 'de',
      next: 'Siguiente',
      previous: 'Anterior',
      submit: 'Obtener Resultados',
      submitting: 'Analizando...',
      
      // Steps
      'step1.title': 'Información Personal',
      'step1.subtitle': 'Cuéntanos sobre ti y tu organización',
      'step2.title': 'Perfil de Empresa',
      'step2.subtitle': 'Ayúdanos a entender la escala y presupuesto de tu negocio',
      'step3.title': 'Procesos Actuales',
      'step3.subtitle': 'Identifica tus procesos manuales y puntos de dolor',
      'step4.title': 'Evaluación de Complejidad',
      'step4.subtitle': 'Evalúa la complejidad técnica de tus procesos',
      'step5.title': 'Infraestructura Tecnológica',
      'step5.subtitle': 'Evalúa tu entorno de TI actual y capacidades',
      'step6.title': 'Objetivos Empresariales',
      'step6.subtitle': 'Define tus objetivos de automatización y criterios de éxito',
      
      // Results
      'results.title': 'Resultados de tu Evaluación RPA',
      'results.subtitle': 'Basado en tu perfil, hemos calculado tu potencial de automatización',
      'results.feasibility': 'Puntuación de Viabilidad',
      'results.ease': 'Facilidad de Implementación',
      'results.roi': 'ROI Potencial',
      'results.recommendation': 'Recomendación',
      'results.high_feasibility': 'Alta Viabilidad - Excelente candidato para automatización',
      'results.medium_feasibility': 'Viabilidad Media - Buenas oportunidades con alguna optimización',
      'results.low_feasibility': 'Baja Viabilidad - Se recomienda optimización de procesos primero',
      'results.easy_implementation': 'Implementación Fácil - Procesos bien definidos y sistemas compatibles',
      'results.moderate_implementation': 'Implementación Moderada - Algunas modificaciones de proceso requeridas',
      'results.complex_implementation': 'Implementación Compleja - Rediseño significativo de procesos necesario',
      'results.high_roi': 'ROI Alto - Retorno esperado > 300% en 2 años',
      'results.medium_roi': 'ROI Medio - Retorno esperado 150-300% en 2 años',
      'results.low_roi': 'ROI Bajo - Retorno esperado < 150% en 2 años',
      'results.investment': 'Inversión Estimada',
      'results.savings': 'Ahorro Anual',
      'results.payback': 'Período de Recuperación',
      'results.cost_comparison': 'Comparación de Costos: QuantumBox vs Competidores',
      'results.quantum_advantage': 'Ventaja QuantumBox',
      'results.download': 'Descargar Informe Completo',
      
      // Cost Comparison
      'comparison.title': '¿Por Qué Elegir QuantumBox Sobre RPA Tradicional?',
      'comparison.subtitle': 'Ve cómo nuestro enfoque de IA Agentiva te ahorra dinero',
      'comparison.quantum': 'QuantumBox AI',
      'comparison.traditional': 'RPA Tradicional',
      'comparison.implementation': 'Costo de Implementación',
      'comparison.licensing': 'Licencia Anual',
      'comparison.maintenance': 'Mantenimiento y Soporte',
      'comparison.training': 'Costos de Entrenamiento',
      'comparison.total_3yr': 'Costo Total a 3 Años',
      'comparison.savings': 'vs Ahorros QuantumBox'
    },
    pt: {
      title: 'Avaliação de Automação RPA',
      subtitle: 'Descubra seu potencial de automação e ROI com nossa avaliação abrangente',
      step: 'Passo',
      of: 'de',
      next: 'Próximo',
      previous: 'Anterior',
      submit: 'Obter Resultados',
      submitting: 'Analisando...',
      
      // Steps
      'step1.title': 'Informações Pessoais',
      'step1.subtitle': 'Conte-nos sobre você e sua organização',
      'step2.title': 'Perfil da Empresa',
      'step2.subtitle': 'Ajude-nos a entender a escala e orçamento do seu negócio',
      'step3.title': 'Processos Atuais',
      'step3.subtitle': 'Identifique seus processos manuais e pontos problemáticos',
      'step4.title': 'Avaliação de Complexidade',
      'step4.subtitle': 'Avalie a complexidade técnica dos seus processos',
      'step5.title': 'Infraestrutura Tecnológica',
      'step5.subtitle': 'Avalie seu ambiente de TI atual e capacidades',
      'step6.title': 'Objetivos Empresariais',
      'step6.subtitle': 'Defina seus objetivos de automação e critérios de sucesso',
      
      // Results
      'results.title': 'Resultados da sua Avaliação RPA',
      'results.subtitle': 'Com base no seu perfil, calculamos seu potencial de automação',
      'results.feasibility': 'Pontuação de Viabilidade',
      'results.ease': 'Facilidade de Implementação',
      'results.roi': 'ROI Potencial',
      'results.recommendation': 'Recomendação',
      'results.high_feasibility': 'Alta Viabilidade - Excelente candidato para automação',
      'results.medium_feasibility': 'Viabilidade Média - Boas oportunidades com alguma otimização',
      'results.low_feasibility': 'Baixa Viabilidade - Recomenda-se otimização de processos primeiro',
      'results.easy_implementation': 'Implementação Fácil - Processos bem definidos e sistemas compatíveis',
      'results.moderate_implementation': 'Implementação Moderada - Algumas modificações de processo necessárias',
      'results.complex_implementation': 'Implementação Complexa - Redesign significativo de processos necessário',
      'results.high_roi': 'ROI Alto - Retorno esperado > 300% em 2 anos',
      'results.medium_roi': 'ROI Médio - Retorno esperado 150-300% em 2 anos',
      'results.low_roi': 'ROI Baixo - Retorno esperado < 150% em 2 anos',
      'results.investment': 'Investimento Estimado',
      'results.savings': 'Economia Anual',
      'results.payback': 'Período de Retorno',
      'results.cost_comparison': 'Comparação de Custos: QuantumBox vs Concorrentes',
      'results.quantum_advantage': 'Vantagem QuantumBox',
      'results.download': 'Baixar Relatório Completo',
      
      // Cost Comparison
      'comparison.title': 'Por Que Escolher QuantumBox Sobre RPA Tradicional?',
      'comparison.subtitle': 'Veja como nossa abordagem de IA Agentiva economiza dinheiro',
      'comparison.quantum': 'QuantumBox AI',
      'comparison.traditional': 'RPA Tradicional',
      'comparison.implementation': 'Custo de Implementação',
      'comparison.licensing': 'Licença Anual',
      'comparison.maintenance': 'Manutenção e Suporte',
      'comparison.training': 'Custos de Treinamento',
      'comparison.total_3yr': 'Custo Total em 3 Anos',
      'comparison.savings': 'vs Economias QuantumBox'
    }
  };

  const tr = (key: string) => translations[language as keyof typeof translations]?.[key as keyof typeof translations.en] || key;

  const calculateScores = () => {
    let feasibilityScore = 0;
    let easeScore = 0;
    let roiScore = 0;

    // Feasibility calculation
    if (formData.manual_processes.length >= 3) feasibilityScore += 25;
    if (formData.process_frequency === 'daily' || formData.process_frequency === 'multiple_daily') feasibilityScore += 25;
    if (formData.process_volume === 'high' || formData.process_volume === 'very_high') feasibilityScore += 25;
    if (formData.data_sources.length <= 3) feasibilityScore += 25;

    // Ease calculation
    if (formData.integration_complexity === 'low' || formData.integration_complexity === 'medium') easeScore += 25;
    if (formData.cloud_readiness === 'high' || formData.cloud_readiness === 'medium') easeScore += 25;
    if (formData.it_support === 'high' || formData.it_support === 'medium') easeScore += 25;
    if (formData.change_management === 'high' || formData.change_management === 'medium') easeScore += 25;

    // ROI calculation
    const revenue = parseInt(formData.annual_revenue?.replace(/[^0-9]/g, '') || '0');
    if (revenue > 10000000) roiScore += 30; // >$10M
    else if (revenue > 1000000) roiScore += 20; // >$1M
    else roiScore += 10;

    if (formData.automation_budget === 'high' || formData.automation_budget === 'medium') roiScore += 25;
    if (formData.primary_objectives.includes('cost_reduction')) roiScore += 25;
    if (formData.timeline === '3_months' || formData.timeline === '6_months') roiScore += 20;

    return { feasibilityScore, easeScore, roiScore };
  };

  const calculateCostComparison = () => {
    const baseEmployees = parseInt(formData.current_employees) || 100;
    const licenseMultiplier = Math.ceil(baseEmployees / 10); // Assume 1 license per 10 employees

    const quantumBox = {
      implementation: 50000,
      licensing: 20000 * licenseMultiplier,
      maintenance: 10000,
      training: 15000,
    };

    const uiPath = {
      implementation: 150000,
      licensing: 45000 * licenseMultiplier,
      maintenance: 35000,
      training: 40000,
    };

    const automationAnywhere = {
      implementation: 180000,
      licensing: 50000 * licenseMultiplier,
      maintenance: 40000,
      training: 50000,
    };

    const bluePrism = {
      implementation: 200000,
      licensing: 55000 * licenseMultiplier,
      maintenance: 45000,
      training: 60000,
    };

    const calculate3YearCost = (costs: any) => {
      return costs.implementation + (costs.licensing * 3) + (costs.maintenance * 3) + costs.training;
    };

    return {
      quantumBox: {
        ...quantumBox,
        total3Year: calculate3YearCost(quantumBox)
      },
      uiPath: {
        ...uiPath,
        total3Year: calculate3YearCost(uiPath)
      },
      automationAnywhere: {
        ...automationAnywhere,
        total3Year: calculate3YearCost(automationAnywhere)
      },
      bluePrism: {
        ...bluePrism,
        total3Year: calculate3YearCost(bluePrism)
      }
    };
  };

  const handleSubmit = async () => {
    console.log('handleSubmit called');
    console.log('Current formData:', formData);
    console.log('Current showResults:', showResults);
    
    setIsSubmitting(true);
    
    try {
      const scores = calculateScores();
      console.log('Calculated scores:', scores);
      
      const costComparison = calculateCostComparison();
      console.log('Cost comparison:', costComparison);
      
      const assessmentData = {
        ...formData,
        feasibility_score: scores.feasibilityScore,
        ease_score: scores.easeScore,
        roi_score: scores.roiScore,
        cost_comparison: costComparison,
        language: language
      };

      console.log('Assessment data:', assessmentData);

      const resultsData = {
        ...scores,
        costComparison,
        id: 'temp-id'
      };
      
      console.log('Setting results to:', resultsData);
      setResults(resultsData);
      
      console.log('Setting showResults to true');
      setShowResults(true);
      
      const message = language === 'en' ? 'Assessment completed successfully!' : 
                     language === 'fr' ? 'Évaluation complétée avec succès!' :
                     language === 'es' ? '¡Evaluación completada exitosamente!' :
                     'Avaliação concluída com sucesso!';
      toast.success(message);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      const errorMessage = language === 'en' ? 'Error submitting assessment' : 
                          language === 'fr' ? 'Erreur lors de la soumission' :
                          language === 'es' ? 'Error al enviar la evaluación' :
                          'Erro ao enviar avaliação';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(20);
    doc.text('RPA Assessment Results', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(`Company: ${formData.company}`, 20, 40);
    doc.text(`Contact: ${formData.name} (${formData.email})`, 20, 50);
    
    // Scores
    doc.setFontSize(16);
    doc.text('Assessment Scores', 20, 70);
    
    doc.setFontSize(12);
    doc.text(`Automation Feasibility: ${results.feasibilityScore}/100`, 20, 85);
    doc.text(`Implementation Ease: ${results.easeScore}/100`, 20, 95);
    doc.text(`3-Year ROI Potential: ${results.roiScore}/100`, 20, 105);
    
    // Cost Comparison
    doc.setFontSize(16);
    doc.text('Cost Comparison (3-Year Total)', 20, 125);
    
    doc.setFontSize(12);
    doc.text(`QuantumBox AI: $${results.costComparison.quantumBox.total3Year.toLocaleString()}`, 20, 140);
    doc.text(`UiPath: $${results.costComparison.uiPath.total3Year.toLocaleString()}`, 20, 150);
    doc.text(`Automation Anywhere: $${results.costComparison.automationAnywhere.total3Year.toLocaleString()}`, 20, 160);
    doc.text(`Blue Prism: $${results.costComparison.bluePrism.total3Year.toLocaleString()}`, 20, 170);
    
    const savings = results.costComparison.uiPath.total3Year - results.costComparison.quantumBox.total3Year;
    doc.setFontSize(14);
    doc.text(`Potential Savings with QuantumBox: $${savings.toLocaleString()}`, 20, 185);
    
    doc.save(`RPA-Assessment-${formData.company}-${Date.now()}.pdf`);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (showResults) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4">
                <CheckCircle className="w-4 h-4 mr-2" />
                {tr('results.title')}
              </Badge>
              <h1 className="text-4xl font-bold mb-4">{tr('results.title')}</h1>
              <p className="text-xl text-muted-foreground">{tr('results.subtitle')}</p>
            </div>

            {/* Score Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 bg-green-500/10 rounded-full w-fit">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                  <CardTitle>{tr('results.feasibility')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-green-600 mb-2">{results.feasibilityScore}/100</div>
                  <Progress value={results.feasibilityScore} className="mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {results.feasibilityScore >= 75 ? 'Excellent' : 
                     results.feasibilityScore >= 50 ? 'Good' : 'Fair'}
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 bg-blue-500/10 rounded-full w-fit">
                    <Clock className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle>{tr('results.ease')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-blue-600 mb-2">{results.easeScore}/100</div>
                  <Progress value={results.easeScore} className="mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {results.easeScore >= 75 ? 'Very Easy' : 
                     results.easeScore >= 50 ? 'Moderate' : 'Complex'}
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 bg-purple-500/10 rounded-full w-fit">
                    <DollarSign className="w-8 h-8 text-purple-600" />
                  </div>
                  <CardTitle>{tr('results.roi')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-purple-600 mb-2">{results.roiScore}/100</div>
                  <Progress value={results.roiScore} className="mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {results.roiScore >= 75 ? 'Excellent ROI' : 
                     results.roiScore >= 50 ? 'Good ROI' : 'Moderate ROI'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Cost Comparison */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">{tr('comparison.title')}</CardTitle>
                <CardDescription>{tr('comparison.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Solution</th>
                        <th className="text-right p-3">{tr('comparison.implementation')}</th>
                        <th className="text-right p-3">{tr('comparison.licensing')}</th>
                        <th className="text-right p-3">{tr('comparison.maintenance')}</th>
                        <th className="text-right p-3">{tr('comparison.training')}</th>
                        <th className="text-right p-3 font-bold">{tr('comparison.total_3yr')}</th>
                        <th className="text-right p-3">{tr('comparison.savings')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b bg-yellow-100 border-yellow-300">
                        <td className="p-3 font-bold text-gray-900 bg-yellow-200">{tr('comparison.quantum')}</td>
                        <td className="text-right p-3 font-semibold text-gray-900">${results.costComparison.quantumBox.implementation.toLocaleString()}</td>
                        <td className="text-right p-3 font-semibold text-gray-900">${results.costComparison.quantumBox.licensing.toLocaleString()}/yr</td>
                        <td className="text-right p-3 font-semibold text-gray-900">${results.costComparison.quantumBox.maintenance.toLocaleString()}/yr</td>
                        <td className="text-right p-3 font-semibold text-gray-900">${results.costComparison.quantumBox.training.toLocaleString()}</td>
                        <td className="text-right p-3 font-bold text-gray-900">${results.costComparison.quantumBox.total3Year.toLocaleString()}</td>
                        <td className="text-right p-3 text-green-600 font-bold bg-green-100">Baseline</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">{tr('comparison.uipath')}</td>
                        <td className="text-right p-3">${results.costComparison.uiPath.implementation.toLocaleString()}</td>
                        <td className="text-right p-3">${results.costComparison.uiPath.licensing.toLocaleString()}/yr</td>
                        <td className="text-right p-3">${results.costComparison.uiPath.maintenance.toLocaleString()}/yr</td>
                        <td className="text-right p-3">${results.costComparison.uiPath.training.toLocaleString()}</td>
                        <td className="text-right p-3 font-bold">${results.costComparison.uiPath.total3Year.toLocaleString()}</td>
                        <td className="text-right p-3 text-red-600">+${(results.costComparison.uiPath.total3Year - results.costComparison.quantumBox.total3Year).toLocaleString()}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">{tr('comparison.automation_anywhere')}</td>
                        <td className="text-right p-3">${results.costComparison.automationAnywhere.implementation.toLocaleString()}</td>
                        <td className="text-right p-3">${results.costComparison.automationAnywhere.licensing.toLocaleString()}/yr</td>
                        <td className="text-right p-3">${results.costComparison.automationAnywhere.maintenance.toLocaleString()}/yr</td>
                        <td className="text-right p-3">${results.costComparison.automationAnywhere.training.toLocaleString()}</td>
                        <td className="text-right p-3 font-bold">${results.costComparison.automationAnywhere.total3Year.toLocaleString()}</td>
                        <td className="text-right p-3 text-red-600">+${(results.costComparison.automationAnywhere.total3Year - results.costComparison.quantumBox.total3Year).toLocaleString()}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">{tr('comparison.blue_prism')}</td>
                        <td className="text-right p-3">${results.costComparison.bluePrism.implementation.toLocaleString()}</td>
                        <td className="text-right p-3">${results.costComparison.bluePrism.licensing.toLocaleString()}/yr</td>
                        <td className="text-right p-3">${results.costComparison.bluePrism.maintenance.toLocaleString()}/yr</td>
                        <td className="text-right p-3">${results.costComparison.bluePrism.training.toLocaleString()}</td>
                        <td className="text-right p-3 font-bold">${results.costComparison.bluePrism.total3Year.toLocaleString()}</td>
                        <td className="text-right p-3 text-red-600">+${(results.costComparison.bluePrism.total3Year - results.costComparison.quantumBox.total3Year).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg">
                  <h4 className="font-bold text-lg text-green-800 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {tr('results.quantum_advantage')}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <p className="flex items-center text-green-700 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Agentic AI reduces implementation complexity by 60%
                      </p>
                      <p className="flex items-center text-green-700 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Lower licensing costs with AI-driven automation
                      </p>
                      <p className="flex items-center text-green-700 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Minimal training requirements (2-3 days vs 2-3 weeks)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <p className="flex items-center text-green-700 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Self-healing and adaptive AI systems
                      </p>
                      <p className="flex items-center text-green-700 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Built-in intelligent exception handling
                      </p>
                      <p className="flex items-center text-green-700 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Continuous optimization through machine learning
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                    <p className="text-yellow-800 font-medium text-center">
                      🚀 Save 40-60% on total automation costs while achieving faster ROI
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button onClick={generatePDF} size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Download className="w-5 h-5 mr-2" />
                {tr('results.download')}
              </Button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">{tr('form.name')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">{tr('form.email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@company.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">{tr('form.phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <Label htmlFor="company">{tr('form.company')} *</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Company Name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">{tr('form.role')} *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ceo">CEO/President</SelectItem>
                    <SelectItem value="cto">CTO/IT Director</SelectItem>
                    <SelectItem value="ops_manager">Operations Manager</SelectItem>
                    <SelectItem value="process_manager">Process Manager</SelectItem>
                    <SelectItem value="business_analyst">Business Analyst</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="industry">{tr('form.industry')} *</Label>
                <Select value={formData.industry} onValueChange={(value) => setFormData({...formData, industry: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="finance">Finance & Banking</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="retail">Retail & E-commerce</SelectItem>
                    <SelectItem value="logistics">Logistics & Supply Chain</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="telecom">Telecommunications</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="company_size">{tr('form.company_size')} *</Label>
                <Select value={formData.company_size} onValueChange={(value) => setFormData({...formData, company_size: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="startup">Startup (1-10 employees)</SelectItem>
                    <SelectItem value="small">Small (11-50 employees)</SelectItem>
                    <SelectItem value="medium">Medium (51-500 employees)</SelectItem>
                    <SelectItem value="large">Large (501-5000 employees)</SelectItem>
                    <SelectItem value="enterprise">Enterprise (5000+ employees)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="annual_revenue">{tr('form.annual_revenue')}</Label>
                <Select value={formData.annual_revenue} onValueChange={(value) => setFormData({...formData, annual_revenue: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select annual revenue" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_1m">Under $1M</SelectItem>
                    <SelectItem value="1m_10m">$1M - $10M</SelectItem>
                    <SelectItem value="10m_100m">$10M - $100M</SelectItem>
                    <SelectItem value="100m_1b">$100M - $1B</SelectItem>
                    <SelectItem value="over_1b">Over $1B</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="current_employees">{tr('form.current_employees')} *</Label>
                <Input
                  id="current_employees"
                  type="number"
                  value={formData.current_employees}
                  onChange={(e) => setFormData({...formData, current_employees: e.target.value})}
                  placeholder="100"
                  required
                />
              </div>
              <div>
                <Label htmlFor="it_budget">{tr('form.it_budget')}</Label>
                <Select value={formData.it_budget} onValueChange={(value) => setFormData({...formData, it_budget: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select IT budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_100k">Under $100K</SelectItem>
                    <SelectItem value="100k_500k">$100K - $500K</SelectItem>
                    <SelectItem value="500k_1m">$500K - $1M</SelectItem>
                    <SelectItem value="1m_5m">$1M - $5M</SelectItem>
                    <SelectItem value="over_5m">Over $5M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="automation_budget">{tr('form.automation_budget')}</Label>
                <Select value={formData.automation_budget} onValueChange={(value) => setFormData({...formData, automation_budget: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select automation budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_50k">Under $50K</SelectItem>
                    <SelectItem value="50k_200k">$50K - $200K</SelectItem>
                    <SelectItem value="200k_500k">$200K - $500K</SelectItem>
                    <SelectItem value="500k_1m">$500K - $1M</SelectItem>
                    <SelectItem value="over_1m">Over $1M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label>{tr('form.manual_processes')} *</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {[
                  'Data Entry', 'Invoice Processing', 'Report Generation', 'Customer Service',
                  'Inventory Management', 'Payroll Processing', 'Compliance Reporting', 
                  'Order Processing', 'Email Management', 'Document Processing'
                ].map((process) => (
                  <div key={process} className="flex items-center space-x-2">
                    <Checkbox
                      id={process}
                      checked={formData.manual_processes.includes(process)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({...formData, manual_processes: [...formData.manual_processes, process]});
                        } else {
                          setFormData({...formData, manual_processes: formData.manual_processes.filter(p => p !== process)});
                        }
                      }}
                    />
                    <Label htmlFor={process} className="text-sm">{process}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>{tr('form.process_volume')} *</Label>
                <RadioGroup value={formData.process_volume} onValueChange={(value) => setFormData({...formData, process_volume: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="volume_low" />
                    <Label htmlFor="volume_low">Low (&lt; 100 transactions/day)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="volume_medium" />
                    <Label htmlFor="volume_medium">Medium (100-1000 transactions/day)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high" id="volume_high" />
                    <Label htmlFor="volume_high">High (1000-10000 transactions/day)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="very_high" id="volume_very_high" />
                    <Label htmlFor="volume_very_high">Very High (&gt; 10000 transactions/day)</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>{tr('form.process_frequency')} *</Label>
                <RadioGroup value={formData.process_frequency} onValueChange={(value) => setFormData({...formData, process_frequency: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="weekly" id="freq_weekly" />
                    <Label htmlFor="freq_weekly">Weekly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="daily" id="freq_daily" />
                    <Label htmlFor="freq_daily">Daily</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="multiple_daily" id="freq_multiple" />
                    <Label htmlFor="freq_multiple">Multiple times per day</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="real_time" id="freq_realtime" />
                    <Label htmlFor="freq_realtime">Real-time/Continuous</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div>
              <Label htmlFor="pain_points">{tr('form.pain_points')} *</Label>
              <Textarea
                id="pain_points"
                value={formData.pain_points}
                onChange={(e) => setFormData({...formData, pain_points: e.target.value})}
                placeholder="Describe your main challenges with current manual processes..."
                rows={4}
                required
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label>{tr('form.data_sources')} *</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {[
                  'Excel/CSV Files', 'Databases', 'Web Applications', 'APIs', 
                  'Email Systems', 'ERPs', 'CRMs', 'Document Management', 
                  'Cloud Applications', 'Legacy Systems'
                ].map((source) => (
                  <div key={source} className="flex items-center space-x-2">
                    <Checkbox
                      id={source}
                      checked={formData.data_sources.includes(source)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({...formData, data_sources: [...formData.data_sources, source]});
                        } else {
                          setFormData({...formData, data_sources: formData.data_sources.filter(s => s !== source)});
                        }
                      }}
                    />
                    <Label htmlFor={source} className="text-sm">{source}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>{tr('form.integration_complexity')} *</Label>
                <RadioGroup value={formData.integration_complexity} onValueChange={(value) => setFormData({...formData, integration_complexity: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="complexity_low" />
                    <Label htmlFor="complexity_low">Low - Simple file-based</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="complexity_medium" />
                    <Label htmlFor="complexity_medium">Medium - Database/API integration</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high" id="complexity_high" />
                    <Label htmlFor="complexity_high">High - Multiple system integration</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="very_high" id="complexity_very_high" />
                    <Label htmlFor="complexity_very_high">Very High - Legacy system integration</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>{tr('form.decision_complexity')} *</Label>
                <RadioGroup value={formData.decision_complexity} onValueChange={(value) => setFormData({...formData, decision_complexity: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="simple" id="decision_simple" />
                    <Label htmlFor="decision_simple">Simple - Rule-based decisions</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="moderate" id="decision_moderate" />
                    <Label htmlFor="decision_moderate">Moderate - Multi-criteria decisions</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="complex" id="decision_complex" />
                    <Label htmlFor="decision_complex">Complex - AI/ML required</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div>
              <Label>{tr('form.compliance_requirements')}</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {[
                  'GDPR', 'HIPAA', 'SOX', 'PCI DSS', 
                  'ISO 27001', 'SOC 2', 'PIPEDA', 'None'
                ].map((compliance) => (
                  <div key={compliance} className="flex items-center space-x-2">
                    <Checkbox
                      id={compliance}
                      checked={formData.compliance_requirements.includes(compliance)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({...formData, compliance_requirements: [...formData.compliance_requirements, compliance]});
                        } else {
                          setFormData({...formData, compliance_requirements: formData.compliance_requirements.filter(c => c !== compliance)});
                        }
                      }}
                    />
                    <Label htmlFor={compliance} className="text-sm">{compliance}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <Label>{tr('form.current_platforms')} *</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {[
                  'Microsoft Office 365', 'Google Workspace', 'Salesforce', 'ServiceNow',
                  'SAP', 'Oracle', 'AWS', 'Azure', 'On-Premise Servers', 'Hybrid Cloud'
                ].map((platform) => (
                  <div key={platform} className="flex items-center space-x-2">
                    <Checkbox
                      id={platform}
                      checked={formData.current_platforms.includes(platform)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({...formData, current_platforms: [...formData.current_platforms, platform]});
                        } else {
                          setFormData({...formData, current_platforms: formData.current_platforms.filter(p => p !== platform)});
                        }
                      }}
                    />
                    <Label htmlFor={platform} className="text-sm">{platform}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>{tr('form.cloud_readiness')} *</Label>
                <RadioGroup value={formData.cloud_readiness} onValueChange={(value) => setFormData({...formData, cloud_readiness: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="cloud_low" />
                    <Label htmlFor="cloud_low">Low - Mostly on-premise</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="cloud_medium" />
                    <Label htmlFor="cloud_medium">Medium - Hybrid environment</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high" id="cloud_high" />
                    <Label htmlFor="cloud_high">High - Cloud-first strategy</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>{tr('form.it_support')} *</Label>
                <RadioGroup value={formData.it_support} onValueChange={(value) => setFormData({...formData, it_support: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="support_low" />
                    <Label htmlFor="support_low">Low - Limited IT team</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="support_medium" />
                    <Label htmlFor="support_medium">Medium - Dedicated IT team</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high" id="support_high" />
                    <Label htmlFor="support_high">High - Large IT department</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div>
              <Label>{tr('form.change_management')} *</Label>
              <RadioGroup value={formData.change_management} onValueChange={(value) => setFormData({...formData, change_management: value})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="change_low" />
                  <Label htmlFor="change_low">Low - Resistance to change</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="medium" id="change_medium" />
                  <Label htmlFor="change_medium">Medium - Open to gradual change</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="high" id="change_high" />
                  <Label htmlFor="change_high">High - Eager to innovate</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <Label>{tr('form.primary_objectives')} *</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {[
                  'Cost Reduction', 'Process Efficiency', 'Error Reduction', 'Compliance',
                  'Customer Experience', 'Employee Satisfaction', 'Scalability', 'Innovation'
                ].map((objective) => (
                  <div key={objective} className="flex items-center space-x-2">
                    <Checkbox
                      id={objective}
                      checked={formData.primary_objectives.includes(objective)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({...formData, primary_objectives: [...formData.primary_objectives, objective]});
                        } else {
                          setFormData({...formData, primary_objectives: formData.primary_objectives.filter(o => o !== objective)});
                        }
                      }}
                    />
                    <Label htmlFor={objective} className="text-sm">{objective}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>{tr('form.timeline')} *</Label>
                <RadioGroup value={formData.timeline} onValueChange={(value) => setFormData({...formData, timeline: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="3_months" id="timeline_3m" />
                    <Label htmlFor="timeline_3m">3 months</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="6_months" id="timeline_6m" />
                    <Label htmlFor="timeline_6m">6 months</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="12_months" id="timeline_12m" />
                    <Label htmlFor="timeline_12m">12 months</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="18_months" id="timeline_18m" />
                    <Label htmlFor="timeline_18m">18+ months</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>{tr('form.roi_expectations')} *</Label>
                <RadioGroup value={formData.roi_expectations} onValueChange={(value) => setFormData({...formData, roi_expectations: value})}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="break_even" id="roi_break_even" />
                    <Label htmlFor="roi_break_even">Break even</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="positive_1yr" id="roi_1yr" />
                    <Label htmlFor="roi_1yr">Positive ROI within 1 year</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high_2yr" id="roi_2yr" />
                    <Label htmlFor="roi_2yr">High ROI within 2 years</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="transformational" id="roi_transform" />
                    <Label htmlFor="roi_transform">Transformational impact</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div>
              <Label>{tr('form.stakeholder_buy_in')} *</Label>
              <RadioGroup value={formData.stakeholder_buy_in} onValueChange={(value) => setFormData({...formData, stakeholder_buy_in: value})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="buyin_low" />
                  <Label htmlFor="buyin_low">Low - Need to build support</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="medium" id="buyin_medium" />
                  <Label htmlFor="buyin_medium">Medium - Some support exists</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="high" id="buyin_high" />
                  <Label htmlFor="buyin_high">High - Strong executive support</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        );

      default:
        return null;
    }
  };


  return (
    <main className="min-h-screen bg-background">
      <Header />
      
      <article className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <header className="text-center mb-12">
            <nav aria-label="Assessment Progress" className="mb-4">
              <Badge variant="outline">
                <Bot className="w-4 h-4 mr-2" />
                {tr('step')} {currentStep} {tr('of')} {totalSteps}
              </Badge>
            </nav>
            <h1 className="text-4xl font-bold mb-4">{tr('title')}</h1>
            <p className="text-xl text-muted-foreground">{tr('subtitle')}</p>
          </header>

          <section aria-labelledby="assessment-form">

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">{tr('step')} {currentStep} {tr('of')} {totalSteps}</span>
              <span className="text-sm text-muted-foreground">{Math.round((currentStep / totalSteps) * 100)}%</span>
            </div>
            <Progress value={(currentStep / totalSteps) * 100} className="h-2" />
          </div>

          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle>{tr(`step${currentStep}.title`)}</CardTitle>
              <CardDescription>{tr(`step${currentStep}.subtitle`)}</CardDescription>
            </CardHeader>
            <CardContent>
              {renderStepContent()}
              
              <div className="flex justify-between mt-8">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {tr('previous')}
                </Button>
                
                <Button
                  onClick={nextStep}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    tr('submitting')
                  ) : currentStep === totalSteps ? (
                    tr('submit')
                  ) : (
                    <>
                      {tr('next')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          </section>
        </div>
      </article>

      <Footer />
    </main>
  );
};

export default RPAAssessment;